// RPTLAYOUT1-F1-S3 (docs/build-briefs/RPTLAYOUT1-F1-S3-guided-report-builder.md; design/mockups/reports-experience.html): a saved
// custom report in the results screen, in the look of the slice 1 results (rx-* classes): a control row, the chart, a totals card
// with change chips, and a sortable table with a pinned Total row. It runs the SAVED report at its exact version as the signed-in
// person; the server re-checks every permission, and every figure on the screen is the server's.
//
// Another screen opens a saved report with mountSavedReportResults(target, host, { id, version }), so the Reports home can use the
// same screen for its saved-reports cards.

import type { CustomReportResultDto } from "./api.ts";
import { scheduleHost, type CannedReportHost } from "./cannedReport.ts";
import { NOT_AVAILABLE, valueLabelsOf, type CatalogDataset } from "./customReportModel.ts";
import { saveBlob } from "./consoleDownload.ts";
import { CHART_WIDTH_SAVED, renderChartCard } from "./reportChart.ts";
import { buildChartModel, chartOf } from "./reportChartModel.ts";
import { compositePresentation } from "./compositeReportModel.ts";
import {
  mayExportSaved,
  maySchedule,
  nextResultSort,
  resultView,
  runMark,
  savedExportRequest,
  savedRunRequest,
  sortedRows,
  type ResultSort,
  type ResultView,
} from "./reportBuilderResultsModel.ts";
import type { FlowKind } from "./reportBuilderFlowModel.ts";
import { mountReportScheduleDialog } from "./reportSchedules.ts";
import { hideHoverHelp, hoverHelp } from "./hoverHelp.ts";
import { createExportButtons } from "./reportExportButtons.ts";
import { exportMenu, type ToolbarItem } from "./reportToolbar.ts";

export interface ReportResultsHost extends CannedReportHost {
  kind: FlowKind;
  /** A permission read from the signed-in person's snapshot. */
  can(permission: string): boolean;
  /** The person's marks route (Recently run). */
  marksPrefix: string;
  /** Leave for the Reports home. */
  back(): void;
  /**
   * REPORTUX1 (baseline 11): open the custom report wizard on a saved report as a new version (its spec, id and current version).
   * Absent when this person may not build custom reports, so the button is not drawn at all.
   */
  editReport?(editing: { spec: unknown; id: string; baseVersion: number }): void;
}

export interface SavedReportInput {
  id: string;
  version?: number;
  /** Shown until the run answers. */
  title?: string;
}

type Status = "loading" | "ok" | "empty" | "busy" | "denied" | "error";

function node<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (className !== undefined) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}
function button(text: string, className: string, action: () => void, testid?: string): HTMLButtonElement {
  const element = node("button", className, text);
  element.type = "button";
  element.addEventListener("click", action);
  if (testid !== undefined) element.dataset["testid"] = testid;
  return element;
}
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isResult(value: unknown): value is CustomReportResultDto {
  return isRecord(value) && Array.isArray(value["rows"]) && Array.isArray(value["columns"]) && isRecord(value["meta"]);
}
function words(reply: { body: unknown }, fallback: string): string {
  return isRecord(reply.body) && typeof reply.body["message"] === "string" ? reply.body["message"] : fallback;
}

export function mountSavedReportResults(target: HTMLElement, host: ReportResultsHost, input: SavedReportInput): { destroy(): void } {
  let alive = true;
  let generation = 0;
  let status: Status = "loading";
  let failure = "";
  let result: CustomReportResultDto | undefined;
  let view: ResultView | undefined;
  let title = input.title ?? "";
  let sort: ResultSort | null = null;
  let notice = "";
  let noticeLink = "";
  /** The notice text that reports something that worked (drawn as plain text, not as an error). */
  let okNotice = "";
  let datasets: CatalogDataset[] = [];
  /** UIFIX1 (SF-1): the one Export menu is open. */
  let menuOpen = false;
  /** UIFIX1 (SF-2): the chart is collapsible and opens on a screen wide enough for it. */
  let chartOpen: boolean | undefined;
  /** REPORTUX1: what the saved report's own record says about editing it, read with each run. */
  let editInfo: { spec: unknown; currentVersion: number; canEdit: boolean } | "unavailable" | undefined;

  const root = node("section", "rx rb-results");
  root.dataset["testid"] = "rbr";
  const screen = node("div", "rx-screen");
  root.append(screen);
  target.append(root);
  const active = () => alive && host.active() && root.isConnected;
  const busy = () => status === "loading";
  // RPTEXPORT1: Export PDF and Export to Google Sheets beside Export CSV, for the saved report on screen.
  const exportButtons = createExportButtons(host, {
    testid: "rbr",
    target: () => {
      if (result === undefined || !mayExportSaved(host.kind, host.can)) return undefined;
      const request = savedExportRequest(host.kind, host.customPrefix, input.id, input.version);
      return { path: request.path, body: request.body };
    },
    disabled: () => busy(),
    notify: (message, link, ok) => {
      notice = message;
      noticeLink = link ?? "";
      okNotice = ok === true ? message : "";
      if (active()) draw();
    },
    redraw: () => {
      if (active()) draw();
    },
  });

  async function run(attempt = 0): Promise<void> {
    const captured = ++generation;
    status = "loading";
    failure = "";
    result = undefined;
    view = undefined;
    sort = null;
    notice = "";
    noticeLink = "";
    draw();
    try {
      const request = savedRunRequest(host.kind, host.customPrefix, input.id, input.version);
      const [reply, catalog, detail] = await Promise.all([
        host.request<unknown>("POST", request.path, request.body),
        host.request<unknown>("GET", `${host.customPrefix}/catalog`).catch(() => undefined),
        host.editReport === undefined ? Promise.resolve(undefined) : host.request<unknown>("GET", `${host.customPrefix}/saved/${encodeURIComponent(input.id)}`).catch(() => undefined),
      ]);
      if (!active() || captured !== generation) return;
      if (host.editReport !== undefined) {
        const body = detail !== undefined && detail.status === 200 && isRecord(detail.body) ? detail.body : undefined;
        const current = body === undefined ? NaN : Number(body["currentVersion"]);
        // A network record carries no canEdit: the build permission already decided that the button is drawn.
        editInfo = body === undefined || !Number.isSafeInteger(current) || body["spec"] === undefined ? "unavailable" : { spec: body["spec"], currentVersion: current, canEdit: body["canEdit"] !== false };
      }
      if (catalog !== undefined && catalog.status === 200 && isRecord(catalog.body) && isRecord(catalog.body["catalog"]) && Array.isArray(catalog.body["catalog"]["datasets"])) {
        datasets = catalog.body["catalog"]["datasets"] as CatalogDataset[];
      }
      if (reply.status === 409 && attempt < 4) {
        // One report runs at a time at a location (DEC-435): the run that holds it is usually the builder's own preview, a moment from done.
        await new Promise((resolve) => setTimeout(resolve, 1500));
        if (!active() || captured !== generation) return;
        void run(attempt + 1);
        return;
      }
      if (reply.status === 409) status = "busy";
      else if (reply.status === 401 || reply.status === 403) status = "denied";
      else if (reply.status !== 200 || !isResult(reply.body)) {
        status = "error";
        failure = words(reply, "This report did not load. Try again.");
      } else {
        result = reply.body;
        const labels = valueLabelsOf(datasets.find((dataset) => dataset.id === result?.meta.dataset));
        view = resultView(result, labels);
        title = result.meta.title !== "" ? result.meta.title : title;
        status = result.rows.length === 0 ? "empty" : "ok";
        void mark(view);
      }
    } catch {
      if (!active() || captured !== generation) return;
      status = "error";
      failure = "This report did not load. Try again.";
    }
    if (active() && captured === generation) draw();
  }
  async function mark(shown: ResultView): Promise<void> {
    try {
      await host.request<unknown>("POST", `${host.marksPrefix}/run`, { reportId: input.id, label: runMark(shown) });
    } catch {
      // Only the Recently run list misses it; the run already shows.
    }
  }
  async function exportCsv(): Promise<void> {
    if (busy() || result === undefined) return;
    const captured = generation;
    notice = "";
    try {
      const request = savedExportRequest(host.kind, host.customPrefix, input.id, input.version);
      const reply = await host.file(request.path, request.body);
      if (!active() || captured !== generation) return;
      if (reply.status === 200 && reply.blob) saveBlob(reply.blob, reply.filename ?? "report.csv");
      else notice = reply.message ?? "Could not download this report.";
    } catch {
      notice = "Could not download this report.";
    }
    if (active() && captured === generation) draw();
  }
  async function schedule(): Promise<void> {
    if (busy() || result === undefined) return;
    const captured = generation;
    let version = input.version;
    if (version === undefined) {
      // The schedule belongs to one saved version: with none named, it is the current one.
      const reply = await host.request<unknown>("GET", `${host.customPrefix}/saved/${encodeURIComponent(input.id)}`);
      if (!active() || captured !== generation) return;
      const current = isRecord(reply.body) ? Number(reply.body["currentVersion"]) : NaN;
      if (reply.status !== 200 || !Number.isSafeInteger(current) || current < 1) {
        notice = words(reply, "Could not open scheduling. Try again.");
        draw();
        return;
      }
      version = current;
    }
    mountReportScheduleDialog(root, scheduleHost(host, () => active() && captured === generation, input.id), { definitionId: input.id, version });
  }

  /** REPORTUX1: Edit report opens the wizard on this saved report. The version on screen, if it is an older one, is what is edited. */
  async function editNow(): Promise<void> {
    if (busy() || host.editReport === undefined || editInfo === undefined || editInfo === "unavailable") return;
    const info = editInfo;
    const captured = generation;
    let spec = info.spec;
    if (input.version !== undefined && input.version !== info.currentVersion) {
      const older = await host.request<unknown>("GET", `${host.customPrefix}/saved/${encodeURIComponent(input.id)}/versions/${input.version}`);
      if (!active() || captured !== generation) return;
      if (older.status !== 200 || !isRecord(older.body) || older.body["spec"] === undefined) {
        notice = words(older, "Could not open this report for editing. Try again.");
        draw();
        return;
      }
      spec = older.body["spec"];
    }
    host.editReport({ spec, id: input.id, baseVersion: info.currentVersion });
  }
  function editControl(): HTMLElement | undefined {
    if (host.editReport === undefined) return undefined;
    const wrap = node("span", "rbr-edit");
    const reason =
      editInfo === undefined ? "Checking whether you can edit this report." : editInfo === "unavailable" ? "This report cannot be edited right now. Try again." : editInfo.canEdit ? "" : "You do not have permission to edit this report.";
    const edit = button("Edit report", "rx-btn", () => void editNow(), "rbr-edit");
    edit.disabled = busy() || reason !== "";
    wrap.append(edit);
    if (reason !== "" && !busy()) {
      const why = node("small", "rbr-edit-reason", reason);
      why.dataset["testid"] = "rbr-edit-reason";
      why.id = `rbr-edit-reason-${input.id}`;
      edit.setAttribute("aria-describedby", why.id);
      wrap.append(why);
    }
    return wrap;
  }

  function draw(): void {
    if (!active()) return;
    hideHoverHelp();
    screen.replaceChildren(crumb(), controls(), body());
  }
  function crumb(): HTMLElement {
    const row = node("div", "rx-crumb");
    row.append(button("‹ Reports", "rx-back", () => host.back(), "rbr-back"), node("h1", undefined, title === "" ? "Report" : title));
    return row;
  }
  function controls(): HTMLElement {
    const row = node("div", "rx-card rx-controls");
    row.dataset["testid"] = "rbr-controls";
    if (view !== undefined) {
      const dates = node("span", "rx-sel-text");
      dates.dataset["testid"] = "rbr-dates";
      dates.append(node("span", undefined, view.dates), node("small", undefined, view.locations));
      row.append(dates);
    }
    row.append(node("span", "rx-grow"));
    const editor = editControl();
    if (editor !== undefined) row.append(editor);
    const ready = !busy() && result !== undefined;
    const canExport = mayExportSaved(host.kind, host.can);
    const canSchedule = maySchedule(host.kind, host.can);
    const items: ToolbarItem[] = [
      { testid: "rbr-export", label: "Export CSV", disabled: !ready || !canExport, reason: canExport ? "" : "You do not have permission to export this report.", run: () => void exportCsv() },
      ...exportButtons.items(),
      { testid: "rbr-schedule", label: "Schedule email", disabled: !ready || !canSchedule, reason: canSchedule ? "" : "You do not have permission to schedule this report.", run: () => void schedule() },
    ];
    const close = (): void => {
      menuOpen = false;
      draw();
    };
    if (menuOpen) {
      const scrim = node("div", "rx-scrim");
      scrim.dataset["testid"] = "rbr-scrim";
      scrim.addEventListener("click", close);
      row.prepend(scrim);
    }
    row.append(
      exportMenu({
        testid: "rbr",
        open: menuOpen,
        toggle: () => {
          menuOpen = !menuOpen;
          draw();
        },
        close,
        items,
        disabled: !ready,
      }),
    );
    if (notice !== "") {
      const text = node("p", notice !== "" && notice === okNotice ? "rx-notice ok" : "rx-notice", notice);
      if (noticeLink !== "") {
        const link = node("a", "rx-notice-link", "Open the sheet");
        link.href = noticeLink;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.dataset["testid"] = "rbr-notice-link";
        text.append(" ", link);
      }
      text.setAttribute("role", "alert");
      row.append(text);
    }
    return row;
  }
  function body(): HTMLElement {
    if (status === "loading") return loading();
    if (status === "busy" || status === "denied" || status === "error") return problem();
    if (status === "empty" || view === undefined || result === undefined) {
      const state = node("div", "rx-card rx-state rx-state-compact");
      state.dataset["testid"] = "rbr-empty";
      state.append(node("h2", undefined, "Nothing recorded in these dates"));
      return state;
    }
    return filled(view, result);
  }
  function loading(): HTMLElement {
    const wrap = node("div", "rx-body");
    wrap.dataset["testid"] = "rbr-loading";
    wrap.setAttribute("role", "status");
    wrap.setAttribute("aria-busy", "true");
    wrap.setAttribute("aria-label", "Loading report");
    const top = node("div", "rx-vis");
    top.append(node("div", "rx-card rx-skel rx-skel-chart"), node("div", "rx-card rx-skel rx-skel-totals"));
    const table = node("div", "rx-card rx-skel-table");
    for (let index = 0; index < 5; index += 1) table.append(node("div", "rx-skel rx-skel-row"));
    wrap.append(top, table);
    return wrap;
  }
  function problem(): HTMLElement {
    const state = node("div", "rx-card rx-state rx-state-compact");
    state.dataset["testid"] = `rbr-${status}`;
    state.setAttribute("role", status === "busy" ? "status" : "alert");
    if (status === "denied") {
      state.append(node("h2", undefined, "No access to this report"), node("p", undefined, "Ask an admin."));
    } else if (status === "busy") {
      const heading = node("h2", undefined, "Another report is running here");
      hoverHelp(heading, "Only one report runs at a time at each location. Yours starts when the current one finishes.");
      state.append(heading, node("p", undefined, "Try again in a moment."), button("Try again", "rx-btn rx-btn-primary", () => void run(), "rbr-retry"));
    } else {
      state.append(node("h2", undefined, "This report did not load"), node("p", undefined, failure), button("Try again", "rx-btn rx-btn-primary", () => void run(), "rbr-retry"));
    }
    return state;
  }
  function filled(shown: ResultView, data: CustomReportResultDto): HTMLElement {
    const wrap = node("div", "rx-body");
    const slot = chartOf(data.spec);
    const chart = slot === undefined ? undefined : buildChartModel(compositePresentation(data), slot, valueLabelsOf(datasets.find((dataset) => dataset.id === data.meta.dataset)));
    const hasChart = chart !== undefined && chart.ok;
    if (shown.figures.length > 1) {
      const totals = node("div", "rx-card rx-totals");
      totals.dataset["testid"] = "rbr-totals";
      for (const figure of shown.figures) {
        const line = node("div", "rx-total");
        line.dataset["testid"] = "rbr-total";
        line.append(node("span", undefined, figure.label));
        const value = node("div", "rx-total-value");
        value.append(node("b", "rx-num", figure.text));
        if (figure.change !== null && figure.change.tone !== "na") value.append(chip(figure.change.text, figure.change.tone));
        line.append(value);
        totals.append(line);
      }
      wrap.append(totals);
    }
    const card = node("div", "rx-card rx-table-card");
    card.dataset["testid"] = "rbr-table";
    const scroll = node("div", "rx-table-scroll");
    scroll.tabIndex = 0;
    scroll.setAttribute("aria-label", `${title} table`);
    const table = node("table", "rx-table");
    table.append(node("caption", "rx-sr", title));
    const head = node("thead");
    const headRow = node("tr");
    // UIFIX1 (SF-3): a column empty for every row is not drawn, and a missing value is blank, never the letters N/A.
    const blank = (text: string): string => (text === NOT_AVAILABLE ? "" : text);
    const kept = new Set(shown.columns.map((_, at) => at).filter((at) => shown.rows.length === 0 || shown.rows.some((row) => blank(row[at]?.text ?? "") !== "")));
    shown.columns.forEach((column, at) => {
      if (!kept.has(at)) return;
      const th = node("th", column.figure ? "num" : "");
      th.scope = "col";
      th.setAttribute("aria-sort", sort?.column === at ? (sort.dir === "asc" ? "ascending" : "descending") : "none");
      const sorter = button(column.label, `rx-th${sort?.column === at ? " on" : ""}`, () => {
        sort = nextResultSort(shown, sort, at);
        draw();
        screen.querySelector<HTMLElement>(`[data-testid="rbr-th-${at}"]`)?.focus();
      }, `rbr-th-${at}`);
      if (sort?.column === at) sorter.append(node("span", "rx-arrow", sort.dir === "asc" ? " ▲" : " ▼"));
      th.append(sorter);
      headRow.append(th);
    });
    head.append(headRow);
    table.append(head);
    const rows = node("tbody");
    for (const row of sortedRows(shown, sort)) {
      const tr = node("tr");
      tr.dataset["testid"] = "rbr-row";
      row.forEach((cell, at) => {
        if (!kept.has(at)) return;
        const figure = shown.columns[at]?.figure === true;
        const td = node("td", figure ? "num rx-num" : "rx-lead");
        if (cell.tone !== undefined && cell.tone !== "na") td.append(chip(cell.text, cell.tone));
        else td.textContent = blank(cell.text);
        tr.append(td);
      });
      rows.append(tr);
    }
    table.append(rows);
    if (shown.total !== null) {
      const tfoot = node("tfoot");
      const tr = node("tr", "rx-total-row");
      tr.dataset["testid"] = "rbr-total-row";
      shown.total.forEach((cell, at) => {
        if (!kept.has(at)) return;
        const td = node("td", shown.columns[at]?.figure === true ? "num rx-num" : "");
        if (cell.tone !== undefined && cell.tone !== "na") td.append(chip(cell.text, cell.tone));
        else td.textContent = blank(cell.text);
        tr.append(td);
      });
      tfoot.append(tr);
      table.append(tfoot);
    }
    scroll.append(table);
    card.append(scroll);
    const parts = [shown.count, shown.note, `Updated ${new Date(data.meta.generatedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`].filter((part) => part !== "");
    const foot = node("div", "rx-foot", parts.join(" · "));
    foot.dataset["testid"] = "rbr-foot";
    card.append(foot);
    wrap.append(card);
    if (chart !== undefined && hasChart) {
      // UIFIX1 (SF-2): the chart sits under the table, can be hidden, and fills the card at 1:1.
      const open = chartOpen ?? (typeof window.matchMedia === "function" ? window.matchMedia("(min-width: 1100px)").matches : true);
      const section = node("div", "rx-chart-section");
      const toggle = button(open ? "Hide chart" : "Show chart", "rx-btn rx-chart-toggle", () => {
        chartOpen = !open;
        draw();
      }, "rbr-chart-toggle");
      toggle.setAttribute("aria-expanded", String(open));
      section.append(toggle);
      if (open) {
        const holder = node("div", "rx-card rx-chart");
        holder.dataset["testid"] = "rbr-chart";
        holder.append(renderChartCard(chart, Math.max(560, Math.min(1400, Math.floor((screen.clientWidth || CHART_WIDTH_SAVED) - 2)))));
        section.append(holder);
      }
      wrap.append(section);
    }
    return wrap;
  }
  function chip(text: string, tone: "up" | "down" | "flat" | "na"): HTMLElement {
    const direction = tone === "up" ? "up" : tone === "down" ? "down" : "flat";
    const arrow = direction === "up" ? "▲" : direction === "down" ? "▼" : "▬";
    const element = node("span", `rx-delta ${direction}`, `${arrow} ${text.replace(/^[+-]/, "")}`);
    element.setAttribute("aria-label", `${direction === "flat" ? "no change" : direction} ${text.replace(/^[+-]/, "")}`);
    return element;
  }

  exportButtons.load();
  void run();
  return {
    destroy() {
      alive = false;
      generation++;
      exportButtons.destroy();
      hideHoverHelp();
      root.remove();
    },
  };
}
